import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/authStore';
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  MessageSquare,
  Settings,
  LogOut,
  ExternalLink,
  BookOpen,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  Plus,
  Languages,
  Receipt
} from 'lucide-react';
import { MobileTopBar } from './MobileTopBar';
import { MobileBottomNav } from './MobileBottomNav';
import { MobileDrawer } from './MobileDrawer';
import { AbnyWebBadge } from '../common/AbnyWebBadge';

export const ClientLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, clientProfile, logout, isStaff } = useAuth();
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/client/dashboard', icon: LayoutDashboard },
    { name: 'Publishing Projects', href: '/client/projects', icon: FolderKanban },
    { name: 'Vernacular Translations', href: '/client/translations', icon: Languages },
    { name: 'Invoices & Billing', href: '/client/invoices', icon: Receipt },
    { name: 'Document Centre', href: '/client/documents', icon: FileText },
    { name: 'Communications', href: '/client/messages', icon: MessageSquare },
    { name: 'Author Settings', href: '/client/settings', icon: Settings },
  ];

  const isActive = (href: string) => {
    if (href === '/client/dashboard' && location.pathname === '/client/dashboard') return true;
    if (href !== '/client/dashboard' && location.pathname.startsWith(href)) return true;
    return false;
  };

  const getPageTitle = () => {
    if (location.pathname.startsWith('/client/translations')) return 'TRANSLATION PROGRESS';
    if (location.pathname.startsWith('/client/invoices')) return 'INVOICES & ACCOUNTS';
    if (location.pathname.startsWith('/client/projects')) return 'PROJECT ROADMAP';
    if (location.pathname.startsWith('/client/documents')) return 'DOCUMENT CENTRE';
    if (location.pathname.startsWith('/client/messages')) return 'EDITORIAL INBOX';
    if (location.pathname.startsWith('/client/settings')) return 'AUTHOR SETTINGS';
    return 'AUTHOR DASHBOARD';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row antialiased font-sans">
      
      {/* Android Mobile Top Bar */}
      <MobileTopBar
        title={getPageTitle()}
        onOpenDrawer={() => setDrawerOpen(true)}
        theme="light"
      />

      {/* Desktop Collapsible Sidebar */}
      <aside
        className={`hidden md:flex flex-col justify-between bg-editorial-dark text-slate-300 border-r border-slate-800 transition-all duration-300 shrink-0 sticky top-0 h-screen ${
          collapsed ? 'w-20' : 'w-64'
        }`}
      >
        <div className="overflow-y-auto overflow-x-hidden">
          {/* Brand header */}
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 overflow-hidden">
              <div className="w-10 h-10 rounded-full overflow-hidden border border-brand-500/50 bg-white p-0.5 shrink-0 shadow-md flex items-center justify-center">
                <img
                  src="/ftt-logo.png"
                  alt="For The Truth Logo"
                  className="w-full h-full object-contain rounded-full"
                />
              </div>
              {!collapsed && (
                <div className="flex flex-col truncate">
                  <span className="font-extrabold text-sm text-white tracking-tight truncate">
                    FOR THE TRUTH
                  </span>
                  <span className="text-[10px] text-brand-400 font-mono font-bold uppercase tracking-wider">
                    Author Portal
                  </span>
                </div>
              )}
            </Link>
          </div>

          {/* User profile card */}
          {!collapsed ? (
            <div className="p-4 mx-3 my-4 rounded-xl bg-slate-900/80 border border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-800/60 border border-brand-500/40 flex items-center justify-center text-brand-300 font-bold text-sm shrink-0">
                  {user?.firstName?.[0] || 'A'}
                  {user?.lastName?.[0] || 'C'}
                </div>
                <div className="overflow-hidden">
                  <div className="text-xs font-bold text-white truncate">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {clientProfile?.companyName || user?.organization || 'Author / Publisher'}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="my-4 flex justify-center">
              <div className="w-10 h-10 rounded-full bg-brand-800/60 border border-brand-500/40 flex items-center justify-center text-brand-300 font-bold text-sm">
                {user?.firstName?.[0] || 'A'}
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <nav className="px-3 space-y-1.5 pb-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  title={collapsed ? item.name : undefined}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all ${
                    active
                      ? 'bg-brand-900/80 text-brand-300 font-bold shadow-xs ring-1 ring-brand-500/40'
                      : 'text-slate-400 hover:text-white hover:bg-slate-850'
                  }`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-brand-400' : 'text-slate-400'}`} />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Sidebar Footer & Collapse Toggle */}
        <div className="p-3 border-t border-slate-800 space-y-2 shrink-0 bg-slate-950">
          
          {/* Powered by ABNY Web */}
          {!collapsed ? (
            <div className="flex justify-center">
              <AbnyWebBadge size="sm" className="w-full justify-between" />
            </div>
          ) : null}

          {isStaff && (
            <Link
              to="/admin"
              title={collapsed ? 'Switch to Admin Panel' : undefined}
              className={`flex items-center justify-between px-3 py-1.5 rounded-xl text-xs bg-amber-950/40 border border-amber-800/40 text-amber-300 hover:bg-amber-900/40 transition-colors ${
                collapsed ? 'justify-center' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
                {!collapsed && <span>Admin Panel</span>}
              </div>
              {!collapsed && <ExternalLink className="w-3 h-3" />}
            </Link>
          )}

          <Link
            to="/"
            title={collapsed ? 'Public Website' : undefined}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs text-slate-400 hover:text-white hover:bg-slate-850 transition-colors ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            <BookOpen className="w-3.5 h-3.5 shrink-0" />
            {!collapsed && <span>Public Website</span>}
          </Link>

          <button
            onClick={logout}
            title={collapsed ? 'Sign Out' : undefined}
            className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs text-rose-400 hover:bg-rose-950/30 transition-colors ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            <LogOut className="w-3.5 h-3.5 shrink-0" />
            {!collapsed && <span>Sign Out</span>}
          </button>

          {/* Collapse Button */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center p-1.5 rounded-lg bg-slate-900 hover:bg-slate-850 text-slate-400 hover:text-white text-xs transition-colors"
            title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0 bg-slate-50 overflow-y-auto pb-24 md:pb-8">
        {/* Desktop Header */}
        <header className="hidden md:flex bg-white border-b border-editorial-border px-8 py-4 items-center justify-between shadow-xs">
          <div>
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-wider font-bold">AUTHOR CLIENT PORTAL</span>
            <h1 className="text-xl font-bold text-slate-900 font-serif">
              For The Truth Publishing
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/submit-enquiry"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs shadow-xs transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Submit Manuscript Proposal</span>
            </Link>
          </div>
        </header>

        {/* Page Content Viewport */}
        <div className="p-4 sm:p-6 md:p-8 max-w-7xl w-full mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Android Mobile Navigation & Bottom Drawer */}
      <MobileBottomNav onOpenDrawer={() => setDrawerOpen(true)} />
      <MobileDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} theme="light" />

    </div>
  );
};
